// react
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import {
  createFileRoute,
  useLocation,
  useNavigate,
} from '@tanstack/react-router';

// material ui icons
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import CloudRoundedIcon from '@mui/icons-material/CloudRounded';
import Co2RoundedIcon from '@mui/icons-material/Co2Rounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import EnergySavingsLeafRoundedIcon from '@mui/icons-material/EnergySavingsLeafRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded';

// material ui
import {
  alpha,
  Autocomplete,
  Avatar,
  Button,
  Card,
  CardHeader,
  Chip,
  Grid,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';

// api
import { useUnitsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/units';
import { useGetBuildingByIdQuery } from '@/api/recalc/endpoints/buildingApiSlice';

// shared components
import PercentageTrendChip from '@/components/ui/chip/PercentageTrendChip';

// feature-components
import CO2CoefficientDialog from '@/features/building-optimization/components/CO2CoefficientDialog';
// relative
import ColumnEnergyStateChart from '@/features/building-optimization/components/ColumnEnergyStateChart';
import HorizontalAverageEnergyBarChart from '@/features/building-optimization/components/HorizontalAverageEnergyBarChart';

// components
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import ErrorBoundary from '@/routes/-ErrorBoundary';

import { DateTime } from 'luxon';

type IdLike = string | number | null | undefined;

type ChartPoint = {
  date?: string;
  start?: string;
  end?: string;
  value?: number | string;
  is_paused?: boolean;
  is_control?: boolean;
  is_baseline?: boolean;
};

type EnergySavings = {
  BillingMeterId?: IdLike;
  TotalEnergyUsage?: number | null;
  TotalEnergySavings?: number | null;
  ActualEnergySavingsPercentage?: number | null;
  AverageEnergyUsageBaselineDay?: number | string | null;
  AverageEnergyUsageControlDay?: number | string | null;
  BaseUnit?: string | null;
  EnergyUsageTimeSeries?: ChartPoint[];
};

type CO2Savings = {
  BillingMeterId?: IdLike;
  TotalCO2Savings?: number | null;
  TotalCO2Emissions?: number | null;
  EstimatedCO2EmissionsWithOptimization?: number | null;
  ActualCO2SavingsPercentage?: number | null;
};

type CO2CoefficientWindow = {
  timestamp_start: string;
  timestamp_end: string;
  value: number;
};

type BillingMeter = {
  BillingMeterId?: IdLike;
  BillingMeterType?: string;
  Type?: string;
  UnitId?: IdLike;
  Name?: string;
};

type Unit = {
  UnitId?: IdLike;
  Name?: string;
  [key: string]: unknown;
};

type BuildingData = {
  AccountId?: IdLike;
  Location?: { Address?: string };
  BillingMeters?: BillingMeter[];
  UserInput?: {
    CO2Coefficients?: {
      Heat?: CO2CoefficientWindow[];
      Gas?: CO2CoefficientWindow[];
      Electricity?: CO2CoefficientWindow[];
    };
  };
  ElectricityMeterSavings?: EnergySavings[] | EnergySavings | null;
  ElectricityCO2Savings?: CO2Savings[] | CO2Savings | null;
  GasMeterSavings?: EnergySavings[] | EnergySavings | null;
  GasCO2Savings?: CO2Savings[] | CO2Savings | null;
  HeatMeterEnergySavings?: EnergySavings[] | null;
  HeatMeterCO2Savings?: CO2Savings[] | null;
  HeatMeterSavings?: Array<EnergySavings | CO2Savings> | null;
  HeatCO2Savings?: CO2Savings[] | CO2Savings | null;
};

type EnrichedMeter = BillingMeter &
  Unit & {
    energySavings: EnergySavings | null;
    co2Savings: CO2Savings | null;
  };

type SavingsLookups = {
  elecEnergyById: Map<string, EnergySavings>;
  elecEnergyFallback: EnergySavings | null;
  elecCO2ById: Map<string, CO2Savings>;
  elecCO2Fallback: CO2Savings | null;
  gasEnergyById: Map<string, EnergySavings>;
  gasEnergyFallback: EnergySavings | null;
  gasCO2ById: Map<string, CO2Savings>;
  gasCO2Fallback: CO2Savings | null;
  heatEnergyById: Map<string, EnergySavings>;
  heatCO2ById: Map<string, CO2Savings>;
  heatCO2Fallback: CO2Savings | null;
};

type RouteState = {
  buildingId?: IdLike;
  enrichedBuilding?: BuildingData;
};

// ------- helpers -------
const toIdKey = (v: IdLike): string => (v == null ? '' : String(v));
const hasEnergyKeys = (o: unknown): o is EnergySavings =>
  typeof o === 'object' &&
  o !== null &&
  ('TotalEnergyUsage' in o || 'EnergyUsageTimeSeries' in o);
const hasCO2Keys = (o: unknown): o is CO2Savings =>
  typeof o === 'object' &&
  o !== null &&
  ('TotalCO2Emissions' in o || 'EstimatedCO2EmissionsWithOptimization' in o);

const formatEnergyUnit = (value: number): string => {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + ' GWh';
  } else if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(1) + ' MWh';
  } else {
    return value.toFixed(0) + ' kWh';
  }
};

const formatCO2Unit = (value: number): string => {
  if (Math.abs(value) >= 1000000) {
    return (value / 1000000).toFixed(1) + ' t CO2e';
  } else if (Math.abs(value) >= 1000) {
    return (value / 1000).toFixed(1) + ' kg CO2e';
  } else {
    return value.toFixed(0) + ' g CO2e';
  }
};

const getCurrentCO2Coefficient = (
  coefficientArray: CO2CoefficientWindow[],
): number | null => {
  const now = DateTime.now(); // Current date-time

  // Find the coefficient that is valid for the current date
  const validCoefficient = coefficientArray.find((item) => {
    const start = DateTime.fromISO(item.timestamp_start);
    const end = DateTime.fromISO(item.timestamp_end);
    return start <= now && end >= now; // Ensure now is within the start and end
  });

  // Return the value if found, otherwise null
  return validCoefficient ? validCoefficient.value : null;
};

const getCO2CoefficientForSelectedMeter = (
  billingMeter: BillingMeter | null,
  building?: BuildingData,
): number | null => {
  if (!billingMeter || !building?.UserInput?.CO2Coefficients) return null;
  let coefficientArray: CO2CoefficientWindow[] = [];

  // Get the appropriate coefficient array based on the BillingMeterType
  switch (billingMeter.BillingMeterType) {
    case 'HeatMeter':
      coefficientArray = building.UserInput.CO2Coefficients.Heat || [];
      break;
    case 'GasMeter':
      coefficientArray = building.UserInput.CO2Coefficients.Gas || [];
      break;
    case 'ElectricityMeter':
      coefficientArray = building.UserInput.CO2Coefficients.Electricity || [];
      break;
    default:
      return null;
  }
  return getCurrentCO2Coefficient(coefficientArray);
};

const getDefaultCO2Coefficient = (
  billingMeter: BillingMeter | null,
): number | null => {
  switch (billingMeter?.BillingMeterType) {
    case 'ElectricityMeter':
      return 144.0; // Default for Electricity
    case 'HeatMeter':
      return 54.0; // Default for Heat
    case 'GasMeter':
      return 198.8; // Default for Gas
    default:
      return null; // If type is not recognized
  }
};

/**
 * Build per-meter lookup maps for each fuel type.
 * Accepts either:
 *  - per-meter arrays (preferred), or
 *  - building-level object (fallback) when array is not provided.
 *
 * For Heat, we allow either explicitly named arrays (HeatMeterEnergySavings / HeatMeterCO2Savings),
 * or autodetect from HeatMeterSavings / HeatCO2Savings by inspecting entry keys.
 */
function buildSavingsLookups(building?: BuildingData): SavingsLookups {
  const {
    ElectricityMeterSavings,
    ElectricityCO2Savings,
    GasMeterSavings,
    GasCO2Savings,

    // Heat can be delivered under different field names depending on the API shape.
    HeatMeterEnergySavings, // optional: per-meter energy array
    HeatMeterCO2Savings, // optional: per-meter CO2 array
    HeatMeterSavings, // optional: ambiguous array (either energy *or* CO2, not mixed)
    HeatCO2Savings, // may be per-meter array OR building-level fallback object
  } = building || {};

  // Electricity
  const elecEnergyById = new Map();
  if (Array.isArray(ElectricityMeterSavings)) {
    for (const s of ElectricityMeterSavings)
      elecEnergyById.set(toIdKey(s?.BillingMeterId), s);
  }
  const elecEnergyFallback = Array.isArray(ElectricityMeterSavings)
    ? null
    : (ElectricityMeterSavings ?? null);

  const elecCO2ById = new Map();
  if (Array.isArray(ElectricityCO2Savings)) {
    for (const s of ElectricityCO2Savings)
      elecCO2ById.set(toIdKey(s?.BillingMeterId), s);
  }
  const elecCO2Fallback = Array.isArray(ElectricityCO2Savings)
    ? null
    : (ElectricityCO2Savings ?? null);

  // Gas
  const gasEnergyById = new Map();
  if (Array.isArray(GasMeterSavings)) {
    for (const s of GasMeterSavings)
      gasEnergyById.set(toIdKey(s?.BillingMeterId), s);
  }
  const gasEnergyFallback = Array.isArray(GasMeterSavings)
    ? null
    : (GasMeterSavings ?? null);

  const gasCO2ById = new Map();
  if (Array.isArray(GasCO2Savings)) {
    for (const s of GasCO2Savings)
      gasCO2ById.set(toIdKey(s?.BillingMeterId), s);
  }
  const gasCO2Fallback = Array.isArray(GasCO2Savings)
    ? null
    : (GasCO2Savings ?? null);

  // Heat
  let heatEnergyArray: EnergySavings[] | null = Array.isArray(
    HeatMeterEnergySavings,
  )
    ? HeatMeterEnergySavings
    : null;
  let heatCO2Array: CO2Savings[] | null = Array.isArray(HeatMeterCO2Savings)
    ? HeatMeterCO2Savings
    : null;

  if (
    !heatEnergyArray &&
    Array.isArray(HeatMeterSavings) &&
    HeatMeterSavings.some(hasEnergyKeys)
  ) {
    heatEnergyArray = HeatMeterSavings;
  }
  if (!heatCO2Array) {
    if (Array.isArray(HeatMeterSavings) && HeatMeterSavings.some(hasCO2Keys)) {
      heatCO2Array = HeatMeterSavings;
    } else if (Array.isArray(HeatCO2Savings)) {
      heatCO2Array = HeatCO2Savings;
    }
  }

  const heatEnergyById = new Map();
  if (Array.isArray(heatEnergyArray)) {
    for (const s of heatEnergyArray)
      heatEnergyById.set(toIdKey(s?.BillingMeterId), s);
  }

  const heatCO2ById = new Map();
  if (Array.isArray(heatCO2Array)) {
    for (const s of heatCO2Array)
      heatCO2ById.set(toIdKey(s?.BillingMeterId), s);
  }

  const heatCO2Fallback = Array.isArray(HeatCO2Savings)
    ? null
    : (HeatCO2Savings ?? null);

  return {
    elecEnergyById,
    elecEnergyFallback,
    elecCO2ById,
    elecCO2Fallback,
    gasEnergyById,
    gasEnergyFallback,
    gasCO2ById,
    gasCO2Fallback,
    heatEnergyById,
    heatCO2ById,
    heatCO2Fallback,
  };
}

function pickSavingsForMeter(
  meter: BillingMeter | null,
  lookups: SavingsLookups,
): { energySavings: EnergySavings | null; co2Savings: CO2Savings | null } {
  if (!meter) return { energySavings: null, co2Savings: null };
  const type = meter.BillingMeterType || meter.Type;
  const id = toIdKey(meter.BillingMeterId);

  switch (type) {
    case 'ElectricityMeter':
      return {
        energySavings:
          lookups.elecEnergyById.get(id) ?? lookups.elecEnergyFallback,
        co2Savings: lookups.elecCO2ById.get(id) ?? lookups.elecCO2Fallback,
      };
    case 'GasMeter':
      return {
        energySavings:
          lookups.gasEnergyById.get(id) ?? lookups.gasEnergyFallback,
        co2Savings: lookups.gasCO2ById.get(id) ?? lookups.gasCO2Fallback,
      };
    case 'HeatMeter':
      return {
        energySavings: lookups.heatEnergyById.get(id) ?? null,
        co2Savings: lookups.heatCO2ById.get(id) ?? lookups.heatCO2Fallback,
      };
    default:
      return { energySavings: null, co2Savings: null };
  }
}

function enrichMeter(
  meter: BillingMeter,
  unit: Unit | null,
  lookups: SavingsLookups,
): EnrichedMeter {
  const { energySavings, co2Savings } = pickSavingsForMeter(meter, lookups);
  return { ...meter, ...(unit || {}), energySavings, co2Savings };
}

export const Route = createFileRoute(
  '/$accountId/_user/predictive-heat-control/$buildingName',
)({
  component: RouteComponent,
  errorComponent: ErrorBoundary,
  staticData: {
    crumb: 'glossary:savings-dashboard',
  },
});

function RouteComponent() {
  const { t } = useTranslation('buildingOptimization');
  const { state } = useLocation();
  const navigate = useNavigate();
  // const dispatch = useDispatch();
  const [openCO2eDialog, setOpenCO2eDialog] = React.useState(false);
  const { buildingId, enrichedBuilding } = (state ?? {}) as RouteState;
  const { data: building } = useGetBuildingByIdQuery(buildingId, {
    skip: !buildingId,
  });
  const account = useSelector(getSelectedAccount);
  const { data: units } = useUnitsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${building?.AccountId})` },
    { skip: !building?.AccountId },
  );
  const [selected, setSelected] = React.useState<EnrichedMeter | null>(null); // single enriched state

  const lookups = React.useMemo(
    () => buildSavingsLookups(enrichedBuilding),
    [enrichedBuilding],
  );

  const options = React.useMemo(() => {
    const meters = (building as BuildingData | undefined)?.BillingMeters || [];
    if (!meters.length || !Array.isArray(units)) return [];
    return meters
      .map((m) => {
        const unit = (units as Unit[]).find((u) => u.UnitId === m.UnitId);
        return unit ? enrichMeter(m, unit, lookups) : null;
      })
      .filter((meter): meter is EnrichedMeter => meter !== null);
  }, [building, units, lookups]);

  React.useEffect(() => {
    if (!options.length) {
      if (selected !== null) setSelected(null);
      return;
    }
    if (!selected) {
      setSelected(options[options.length - 1]);
      return;
    }
    const id = toIdKey(selected.BillingMeterId);
    const fresh = options.find((o) => toIdKey(o.BillingMeterId) === id);
    setSelected(fresh || options[options.length - 1]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  React.useEffect(() => {
    if (!selected) return;
    const meters = (building as BuildingData | undefined)?.BillingMeters || [];
    const raw = meters.find(
      (m) => toIdKey(m.BillingMeterId) === toIdKey(selected.BillingMeterId),
    );
    if (!raw) {
      setSelected(options[0] || null);
      return;
    }
    const unit = Array.isArray(units)
      ? (units as Unit[]).find((u) => u.UnitId === raw.UnitId)
      : null;
    setSelected(enrichMeter(raw, unit, lookups));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building, units, lookups]);

  const co2eCoefficient = React.useMemo(
    () => getCO2CoefficientForSelectedMeter(selected, building),
    [selected, building],
  );
  const defaultCoefficient = React.useMemo(
    () => getDefaultCO2Coefficient(selected),
    [selected],
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (account?.AccountId !== building?.AccountId) {
        navigate({ to: '/predictive-heat-control', replace: true });
      }
    }, 500); // Add a 500ms delay

    return () => clearTimeout(timeout); // Cleanup timeout on unmount or dependency change
  }, [account, building, navigate]);

  const handleCO2eCoefficientChange = () => {
    setOpenCO2eDialog(true);
  };

  if (!building) return null;

  return (
    <>
      <Stack mb={2}>
        <Typography variant="h5" fontWeight={600}>
          {building?.Location?.Address}
        </Typography>
      </Stack>

      <Grid container spacing={2} mb={2}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Card sx={{ p: 2, height: '100%' }}>
            <CardHeader
              sx={{ p: 0 }}
              avatar={
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha('#c2dd00', 0.1),
                    borderRadius: 3,
                  }}
                >
                  <SpeedRoundedIcon sx={{ color: '#96b100' }} />
                </Avatar>
              }
              action={
                <Tooltip
                  title={t('buildingPage.metrics.actualConsumption.tooltip')}
                  placement="top"
                  arrow
                >
                  <InfoRoundedIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                      ml: 1,
                    }}
                    fontSize="small"
                  />
                </Tooltip>
              }
            />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="textSecondary"
              gutterBottom
            >
              {t('buildingPage.metrics.actualConsumption.title')}
            </Typography>

            {selected ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <Typography variant="h5" color="text.primary">
                  {selected.energySavings &&
                  selected.energySavings.TotalEnergyUsage != null
                    ? formatEnergyUnit(selected.energySavings.TotalEnergyUsage)
                    : '—'}
                </Typography>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('buildingPage.metrics.noMeterSelected')}
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Card sx={{ p: 2, height: '100%' }}>
            <CardHeader
              sx={{ p: 0 }}
              avatar={
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha('#c2dd00', 0.1),
                    borderRadius: 3,
                  }}
                >
                  <EnergySavingsLeafRoundedIcon sx={{ color: '#96b100' }} />
                </Avatar>
              }
              action={
                <Tooltip
                  title={t('buildingPage.metrics.effectiveSavings.tooltip')}
                  placement="top"
                  arrow
                >
                  <InfoRoundedIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                      ml: 1,
                    }}
                    fontSize="small"
                  />
                </Tooltip>
              }
            />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="textSecondary"
              gutterBottom
            >
              {t('buildingPage.metrics.effectiveSavings.title')}
            </Typography>
            {selected ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <Typography variant="h5" color="text.primary">
                  {selected.energySavings &&
                  selected.energySavings.TotalEnergySavings != null
                    ? formatEnergyUnit(
                        selected.energySavings.TotalEnergySavings,
                      )
                    : '—'}
                </Typography>
                {selected.energySavings &&
                selected.energySavings.ActualEnergySavingsPercentage != null ? (
                  <PercentageTrendChip
                    value={selected.energySavings.ActualEnergySavingsPercentage}
                  />
                ) : null}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('buildingPage.metrics.noMeterSelected')}
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Card sx={{ p: 2, height: '100%' }}>
            <CardHeader
              sx={{ p: 0 }}
              avatar={
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha('#c2dd00', 0.1),
                    borderRadius: 3,
                  }}
                >
                  <CloudRoundedIcon sx={{ color: '#96b100' }} />
                </Avatar>
              }
              action={
                <Tooltip
                  title={t('buildingPage.metrics.co2Savings.tooltip')}
                  placement="top"
                  arrow
                >
                  <InfoRoundedIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                      ml: 1,
                    }}
                    fontSize="small"
                  />
                </Tooltip>
              }
            />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="textSecondary"
              gutterBottom
            >
              {t('buildingPage.metrics.co2Savings.title')}
            </Typography>
            {selected ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <Typography variant="h5" color="text.primary">
                  {selected.co2Savings &&
                  selected.co2Savings.TotalCO2Savings != null
                    ? formatCO2Unit(selected.co2Savings.TotalCO2Savings)
                    : '—'}
                </Typography>
                {/* {selected.co2Savings &&
                  selected.co2Savings.ActualCO2SavingsPercentage != null ? (
                    <PercentageTrendChip
                      value={selected.co2Savings.ActualCO2SavingsPercentage}
                    />
                  ) : null} */}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('buildingPage.metrics.noMeterSelected')}
              </Typography>
            )}
          </Card>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 3,
          }}
        >
          <Card sx={{ p: 2, height: '100%' }}>
            <CardHeader
              sx={{ p: 0 }}
              avatar={
                <Avatar
                  variant="rounded"
                  sx={{
                    bgcolor: alpha('#c2dd00', 0.1),
                    borderRadius: 3,
                  }}
                >
                  <Co2RoundedIcon sx={{ color: '#96b100' }} />
                </Avatar>
              }
              action={
                <Tooltip
                  title={t('buildingPage.metrics.totalCO2Impact.tooltip')}
                  placement="top"
                  arrow
                >
                  <InfoRoundedIcon
                    sx={{
                      color: (theme) =>
                        theme.palette.mode === 'light' ? 'lightgray' : 'gray',
                      ml: 1,
                    }}
                    fontSize="small"
                  />
                </Tooltip>
              }
            />
            <Typography
              variant="subtitle1"
              fontWeight={600}
              color="textSecondary"
              gutterBottom
            >
              {t('buildingPage.metrics.totalCO2Impact.title')}
            </Typography>
            {selected ? (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <Typography variant="h5" color="text.primary">
                  {selected.co2Savings &&
                  selected.co2Savings.TotalCO2Emissions != null
                    ? formatCO2Unit(selected.co2Savings.TotalCO2Emissions)
                    : '—'}
                </Typography>
                {co2eCoefficient ? (
                  <Chip
                    variant="filled"
                    size="small"
                    label={`${co2eCoefficient.toFixed(1)} g/kWh`}
                    icon={<EditRoundedIcon />}
                    onClick={handleCO2eCoefficientChange}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                    }}
                  />
                ) : (
                  <Chip
                    variant="filled"
                    size="small"
                    label={
                      defaultCoefficient != null
                        ? `${defaultCoefficient.toFixed(1)} g/kWh`
                        : '—'
                    }
                    icon={<EditRoundedIcon />}
                    onClick={handleCO2eCoefficientChange}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 2,
                    }}
                  />
                )}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {t('buildingPage.metrics.noMeterSelected')}
              </Typography>
            )}
          </Card>
        </Grid>
      </Grid>
      <Autocomplete
        sx={{ justifyContent: 'end', mt: 2, mb: 2 }}
        size="small"
        options={options}
        getOptionLabel={(opt) =>
          opt?.Name ? `${opt.Name}` : `#${opt?.BillingMeterId ?? ''}`
        }
        value={selected}
        onChange={(e, newVal) => setSelected(newVal || null)}
        isOptionEqualToValue={(opt, val) =>
          toIdKey(opt?.BillingMeterId) === toIdKey(val?.BillingMeterId)
        }
        renderInput={(params) => (
          <TextField
            variant="outlined"
            {...params}
            // label="Vælg måler"
            sx={{ bgcolor: 'background.paper' }}
          />
        )}
      />
      <Stack spacing={2} mb={2}>
        <HorizontalAverageEnergyBarChart
          selected={selected}
          energySavings={selected?.energySavings}
          title={t('buildingPage.charts.averageUsage')}
          unit={selected?.energySavings?.BaseUnit ?? 'kWh'}
          categoryLabel={t('buildingPage.charts.energyLabel')}
          mode={undefined}
        />
        <Card>
          <ColumnEnergyStateChart
            title={t('buildingPage.charts.dailyConsumption')}
            height={400}
            data={selected?.energySavings?.EnergyUsageTimeSeries || []}
            yAxisTitle={t('buildingPage.charts.energyLabel')}
            mode={undefined}
          />
        </Card>
      </Stack>
      <Stack direction="row" spacing={2} justifyContent="flex-start">
        <Button
          variant="text"
          sx={{
            fontSize: '16px',
            color: 'text.secondary',
            ':hover': { color: 'primary.main', backgroundColor: 'transparent' },
            '&:hover .arrow-icon': {
              transform: 'translateX(-4px)',
              color: 'primary.main',
            },
          }}
          onClick={() => navigate({ to: '..' })}
        >
          <ArrowBackRoundedIcon
            className="arrow-icon"
            sx={{
              mr: 1,
              transition: 'transform 0.2s ease-in-out, color 0.2s ease-in-out',
            }}
          />
          {t('buildingPage.navigation.goBack')}
        </Button>
      </Stack>

      <CO2CoefficientDialog
        open={openCO2eDialog}
        onClose={() => setOpenCO2eDialog(false)}
        building={building}
        selectedMeter={selected}
        defaultCoefficient={defaultCoefficient}
      />
    </>
  );
}
